import { useEffect, useMemo, useState } from 'react';
import { Alert, Form, Input, InputNumber, Modal, Radio, Spin } from 'antd';
import { validateFormula, type FieldInfo } from '@lens/formula';
import type { ViewComputedColumnConfig } from '@lens/shared';
import { listFields } from '../item-types/api.ts';

export interface ViewComputedColumnModalProps {
  open: boolean;
  spaceId: string;
  itemTypeIds: string[];
  itemTypeNames: Record<string, string>;
  onClose: () => void;
  onSubmit: (cfg: Omit<ViewComputedColumnConfig, 'key'>) => Promise<void> | void;
}

const RETURN_TYPE_OPTIONS: Array<{
  value: ViewComputedColumnConfig['returnType'];
  label: string;
}> = [
  { value: 'number', label: 'Число' },
  { value: 'text', label: 'Текст' },
];

const VIEW_COMPUTED_FIELD_SLUG = '__view_computed__';

export function extractViewComputedColumns(config: unknown): ViewComputedColumnConfig[] {
  const cfg = config as { columns?: unknown } | null;
  if (!cfg || !Array.isArray(cfg.columns)) return [];
  return cfg.columns.filter(
    (column): column is ViewComputedColumnConfig =>
      !!column &&
      typeof column === 'object' &&
      (column as { kind?: unknown }).kind === 'view_computed',
  );
}

export function ViewComputedColumnModal({
  open,
  spaceId,
  itemTypeIds,
  itemTypeNames,
  onClose,
  onSubmit,
}: ViewComputedColumnModalProps): JSX.Element {
  const [label, setLabel] = useState('');
  const [formula, setFormula] = useState('');
  const [returnType, setReturnType] = useState<ViewComputedColumnConfig['returnType']>('number');
  const [numberDecimals, setNumberDecimals] = useState(0);
  const [fieldInfos, setFieldInfos] = useState<FieldInfo[]>([]);
  const [fieldsLoading, setFieldsLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [formulaError, setFormulaError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setLabel('');
    setFormula('');
    setReturnType('number');
    setNumberDecimals(0);
    setFieldInfos([]);
    setError(null);
    setFormulaError(null);
  }, [open]);

  useEffect(() => {
    if (!open || itemTypeIds.length === 0) return;
    let cancelled = false;
    setFieldsLoading(true);
    Promise.all(itemTypeIds.map((itemTypeId) => listFields(spaceId, itemTypeId)))
      .then((fieldGroups) => {
        if (cancelled) return;
        const unique = new Map<string, FieldInfo>();
        for (const fields of fieldGroups) {
          for (const field of fields) {
            if (!unique.has(field.slug)) {
              unique.set(field.slug, {
                slug: field.slug,
                fieldType: field.fieldType === 'computed' ? 'number' : field.fieldType,
              });
            }
          }
        }
        setFieldInfos([...unique.values()]);
      })
      .catch(() => {
        if (cancelled) return;
        setFieldInfos([]);
        setError('Не удалось загрузить поля для проверки формулы');
      })
      .finally(() => {
        if (!cancelled) setFieldsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, spaceId, itemTypeIds]);

  const itemTypeSummary = useMemo(
    () => itemTypeIds.map((id) => itemTypeNames[id] ?? id).join(', '),
    [itemTypeIds, itemTypeNames],
  );
  const availableFieldSlugs = useMemo(
    () => fieldInfos.map((field) => field.slug),
    [fieldInfos],
  );

  const submitDisabled = !label.trim() || !formula.trim() || fieldsLoading || submitting;

  const handleOk = async () => {
    if (submitDisabled) return;
    setSubmitting(true);
    setError(null);
    setFormulaError(null);
    try {
      validateFormula(formula.trim(), fieldInfos, VIEW_COMPUTED_FIELD_SLUG);
    } catch (err) {
      setFormulaError(`Ошибка в формуле: ${err instanceof Error ? err.message : 'некорректное выражение'}`);
      setSubmitting(false);
      return;
    }

    try {
      await onSubmit({
        kind: 'view_computed',
        label: label.trim(),
        formula: formula.trim(),
        returnType,
        ...(returnType === 'number' ? { numberDecimals } : {}),
      });
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Не удалось сохранить колонку');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      open={open}
      title="Расчётная колонка"
      okText="Добавить"
      cancelText="Отмена"
      onCancel={onClose}
      onOk={handleOk}
      okButtonProps={{ disabled: submitDisabled, loading: submitting }}
      destroyOnHidden
    >
      {error ? <Alert type="error" message={error} style={{ marginBottom: 12 }} /> : null}
      <Form layout="vertical">
        <Form.Item label="Типы записей" extra={fieldsLoading ? <Spin size="small" /> : null}>
          <Input value={itemTypeSummary} disabled />
        </Form.Item>
        <Form.Item label="Заголовок" required>
          <Input
            aria-label="Заголовок"
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            maxLength={200}
            placeholder="Например, Итоговый приоритет"
          />
        </Form.Item>
        <Form.Item
          label="Формула"
          required
          validateStatus={formulaError ? 'error' : undefined}
          help={
            formulaError ?? (
              <div>
                <div>Ссылки на поля: {'{slug}'}. Доступны IF, ROUND, MIN, MAX, ABS, SCALE, CONCAT, FORMAT_NUMBER, COALESCE.</div>
                <div style={{ marginTop: 6 }}>
                  <strong>Доступные поля:</strong>{' '}
                  {availableFieldSlugs.length > 0 ? (
                    availableFieldSlugs.map((slug) => (
                      <code key={slug} style={{ marginRight: 8 }}>
                        {slug}
                      </code>
                    ))
                  ) : (
                    <span style={{ color: '#999' }}>нет доступных полей</span>
                  )}
                </div>
              </div>
            )
          }
        >
          <Input.TextArea
            aria-label="Формула"
            rows={4}
            value={formula}
            onChange={(e) => {
              setFormula(e.target.value);
              setFormulaError(null);
            }}
            style={{ fontFamily: 'monospace' }}
            placeholder="ROUND({score} * 1.2, 2)"
          />
        </Form.Item>
        <Form.Item label="Тип результата" required>
          <Radio.Group
            options={RETURN_TYPE_OPTIONS}
            value={returnType}
            onChange={(e) => setReturnType(e.target.value as ViewComputedColumnConfig['returnType'])}
            optionType="button"
            buttonStyle="solid"
          />
        </Form.Item>
        {returnType === 'number' ? (
          <Form.Item label="Знаков после запятой">
            <InputNumber
              aria-label="Знаков после запятой"
              min={0}
              max={6}
              value={numberDecimals}
              onChange={(value) => setNumberDecimals(typeof value === 'number' ? value : 0)}
              style={{ width: '100%' }}
            />
          </Form.Item>
        ) : null}
      </Form>
    </Modal>
  );
}
